import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as backupService from '../../services/backupService';

export const createBackup = createAsyncThunk(
  'backups/createBackup',
  async (options, { rejectWithValue }) => {
    try {
      const res = await backupService.createBackup(options);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create backup');
    }
  }
);

export const fetchBackups = createAsyncThunk(
  'backups/fetchBackups',
  async (params, { rejectWithValue }) => {
    try {
      const res = await backupService.getBackups(params);
      const body = res.data;
      return {
        backups: body.data || body.backups || [],
        totalPages: body.pagination?.totalPages || body.totalPages || 1,
        currentPage: body.pagination?.page || body.page || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch backups');
    }
  }
);

export const fetchBackup = createAsyncThunk(
  'backups/fetchBackup',
  async (id, { rejectWithValue }) => {
    try {
      const res = await backupService.getBackup(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch backup');
    }
  }
);

export const downloadBackup = createAsyncThunk(
  'backups/downloadBackup',
  async (id, { rejectWithValue }) => {
    try {
      const res = await backupService.downloadBackup(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to download backup');
    }
  }
);

export const restoreBackup = createAsyncThunk(
  'backups/restoreBackup',
  async ({ id, options }, { rejectWithValue }) => {
    try {
      const res = await backupService.restoreBackup(id, options);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to restore backup');
    }
  }
);

export const deleteBackup = createAsyncThunk(
  'backups/deleteBackup',
  async (id, { rejectWithValue }) => {
    try {
      await backupService.deleteBackup(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete backup');
    }
  }
);

export const getBackupSettings = createAsyncThunk(
  'backups/getBackupSettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await backupService.getBackupSettings();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch backup settings');
    }
  }
);

export const updateBackupSettings = createAsyncThunk(
  'backups/updateBackupSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const res = await backupService.updateBackupSettings(settings);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update backup settings');
    }
  }
);

export const testEmail = createAsyncThunk(
  'backups/testEmail',
  async (_, { rejectWithValue }) => {
    try {
      const res = await backupService.testEmail();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Email test failed');
    }
  }
);

export const testCloud = createAsyncThunk(
  'backups/testCloud',
  async (_, { rejectWithValue }) => {
    try {
      const res = await backupService.testCloud();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Cloud test failed');
    }
  }
);

export const getBackupSchedule = createAsyncThunk(
  'backups/getBackupSchedule',
  async (_, { rejectWithValue }) => {
    try {
      const res = await backupService.getBackupSchedule();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch schedule');
    }
  }
);

export const updateBackupSchedule = createAsyncThunk(
  'backups/updateBackupSchedule',
  async (schedule, { rejectWithValue }) => {
    try {
      const res = await backupService.updateBackupSchedule(schedule);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update schedule');
    }
  }
);

export const getBackupStatus = createAsyncThunk(
  'backups/getBackupStatus',
  async (_, { rejectWithValue }) => {
    try {
      const res = await backupService.getBackupStatus();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch backup status');
    }
  }
);

const initialState = {
  backups: [],
  backup: null,
  settings: null,
  schedule: null,
  status: null,
  loading: false,
  error: null,
};

const backupSlice = createSlice({
  name: 'backups',
  initialState,
  reducers: {
    clearBackup: (state) => {
      state.backup = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBackup.fulfilled, (state, action) => {
        state.loading = false;
        state.backup = action.payload.backup || action.payload;
        state.backups.unshift(state.backup);
      })
      .addCase(createBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBackups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBackups.fulfilled, (state, action) => {
        state.loading = false;
        state.backups = action.payload.backups || action.payload.data || action.payload;
      })
      .addCase(fetchBackups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBackup.fulfilled, (state, action) => {
        state.loading = false;
        state.backup = action.payload.backup || action.payload;
      })
      .addCase(fetchBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(downloadBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadBackup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(restoreBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreBackup.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
      })
      .addCase(restoreBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteBackup.fulfilled, (state, action) => {
        state.backups = state.backups.filter((b) => b._id !== action.payload);
        if (state.backup?._id === action.payload) state.backup = null;
      })
      .addCase(getBackupSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBackupSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.settings || action.payload;
      })
      .addCase(getBackupSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBackupSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBackupSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.settings || action.payload;
      })
      .addCase(updateBackupSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(testEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(testEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(testCloud.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testCloud.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(testCloud.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getBackupSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBackupSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.schedule = action.payload.schedule || action.payload;
      })
      .addCase(getBackupSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBackupSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBackupSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.schedule = action.payload.schedule || action.payload;
      })
      .addCase(updateBackupSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getBackupStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBackupStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status || action.payload;
      })
      .addCase(getBackupStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearBackup, clearError } = backupSlice.actions;
export default backupSlice.reducer;
